import React, { useEffect, useMemo, useState } from "react";
import styles from "../Styles/Documents.module.css";
import { useSelectedCompany } from "../Commoncontext/SelectedCompanyContext";
import { useUsers } from "../Commoncontext/UsersContext";
import { useDocuments } from "../Commoncontext/DocumentsContext";
import DocumentTypeIcon from "../Images/DocumentTypeIcon";
import DocumentUploadModal from "./DocumentUploadModal";
import IconDocuments from "../Images/IconDocuments";
import IconWizard from "../Images/IconWizardMin";
import IconButton from "../Images/IconButton";

const CATEGORY_OPTIONS = [
  "Hallinto",
  "Pöytäkirjat",
  "Ylläpito ja huolto",
  "Säännöt ja ohjeet",
  "Talous",
  "Sopimukset",
  "Muut",
];

const ITEMS_PER_PAGE = 9;

/* ----------------------------- */
/* Helpers                       */
/* ----------------------------- */
function formatDate(ts) {
  if (!ts) return "—";

  try {
    if (ts.seconds) {
      return new Date(ts.seconds * 1000).toLocaleDateString("fi-FI");
    }
    if (ts._seconds) {
      return new Date(ts._seconds * 1000).toLocaleDateString("fi-FI");
    }
    return new Date(ts).toLocaleDateString("fi-FI");
  } catch {
    return "—";
  }
}

export default function Documents() {
  const { selectedCompanyId } = useSelectedCompany();
  const { user } = useUsers();
  const { documents, fetchDocuments, uploadDocument, loading } =
    useDocuments();

  const [selectedCategory, setSelectedCategory] = useState("Kaikki");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (selectedCompanyId) fetchDocuments(selectedCompanyId);
  }, [selectedCompanyId, fetchDocuments]);

  /* Resetoi sivu kun suodatus muuttuu */
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  const categoryPills = useMemo(() => {
    const set = new Set(CATEGORY_OPTIONS);
    documents.forEach((d) => d.category && set.add(d.category));
    return ["Kaikki", ...Array.from(set)];
  }, [documents]);

  const filteredDocs = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return documents.filter((doc) => {
      if (
        selectedCategory !== "Kaikki" &&
        doc.category !== selectedCategory
      )
        return false;
      if (!q) return true;
      return (
        doc.name?.toLowerCase().includes(q) ||
        doc.category?.toLowerCase().includes(q) ||
        (doc.tags || []).join(" ").toLowerCase().includes(q) ||
        (doc.ai?.suggestedTags || []).join(" ").toLowerCase().includes(q)
      );
    });
  }, [documents, selectedCategory, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredDocs.length / ITEMS_PER_PAGE)
  );

  const pagedDocs = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredDocs.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredDocs, currentPage]);

  /* Synkkaa selectedDoc sivutuksen ja datapäivitysten kanssa */
  useEffect(() => {
    if (!pagedDocs.length) return;

    if (!selectedDoc) {
      setSelectedDoc(pagedDocs[0]);
      return;
    }

    const updated = pagedDocs.find((d) => d.id === selectedDoc.id);
    if (!updated) {
      setSelectedDoc(pagedDocs[0]);
    } else if (updated !== selectedDoc) {
      setSelectedDoc(updated);
    }
  }, [pagedDocs, selectedDoc]);

 const handleUpload = async (file, { category, visibility }) => {
  await uploadDocument(selectedCompanyId, file, {
    category,
    visibility, // 👈 UUSI

    uploadedBy: user?.uid || null,
    uploadedByName: user?.displayName || user?.email || null,
  });
};

  return (
    <div className={styles.page}>
      <div className={styles.contentWide}>
        {/* OTSIKKO */}
        <div className={styles.pageTitle}>
          <div className={styles.titleIcon}>
            <IconDocuments />
          </div>
          <h1>Dokumentit</h1>
          <div className={styles.mlAuto}>
            <button
              className={styles.primaryBtn}
              onClick={() => setShowUploadModal(true)}
            >
              + Lisää dokumentti
            </button>
          </div>
        </div>

        <div className={styles.mainContainer}>
          {/* KATEGORIAT */}
          <div className={styles.categoryRow}>
            {categoryPills.map((cat) => (
              <button
                key={cat}
                className={`${styles.catPill} ${
                  selectedCategory === cat ? styles.catPillActive : ""
                }`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* HAKU */}
          <div className={styles.searchWrap}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#f4a100"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className={styles.searchInput}
              placeholder="Hae dokumenteista…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* SISÄLTÖ */}
          <div className={styles.splitLayout}>
            {/* VASEN: LISTA */}
            <div>
              {loading && (
                <div className={styles.loadingRow}>Ladataan…</div>
              )}

              {!loading && pagedDocs.length === 0 && (
                <div className={styles.emptyRow}>
                  Ei dokumentteja valituilla ehdoilla.
                </div>
              )}

              {pagedDocs.map((doc) => (
                <button
                  key={doc.id}
                  className={`${styles.docRow} ${
                    selectedDoc?.id === doc.id
                      ? styles.docRowActive
                      : ""
                  }`}
                  onClick={() => setSelectedDoc(doc)}
                >
                  <DocumentTypeIcon filename={doc.name} />
                  <div className={styles.docMeta}>
                    <div className={styles.docName}>{doc.name}</div>
                    <div className={styles.docSub}>
                      {doc.category || "—"} ·{" "}
                      {Math.round((doc.sizeBytes || 0) / 1024)} kt
                      {doc.ai?.docType && <> · {doc.ai.docType}</>}
                    </div>
                  </div>
                </button>
              ))}

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div className={styles.paginationRow}>
                  <IconButton
                    direction="left"
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((p) => Math.max(1, p - 1))
                    }
                  >
                  </IconButton>

                  <span className={styles.pageInfo}>
                    Sivu {currentPage} / {totalPages}
                  </span>

                  <IconButton
                    direction="right"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((p) =>
                        Math.min(totalPages, p + 1)
                      )
                    }
                  >
                  </IconButton>
                </div>
              )}
            </div>

            {/* OIKEA: DETAIL */}
            <div className={styles.docDetail}>
              {!selectedDoc ? (
                <div className={styles.placeholder}>
                  Valitse dokumentti nähdäksesi tiedot
                </div>
              ) : (
                <>
                  <div className={styles.detailHeader}>
                    <DocumentTypeIcon filename={selectedDoc.name} />
                    <div>
                      <h2>{selectedDoc.name}</h2>
                      <div className={styles.detailCategory}>
                        {selectedDoc.category}
                          <> · {Math.round((selectedDoc.sizeBytes || 0) / 1024)} kt</>
                          <> · Muokattu {formatDate(selectedDoc.modified)}</>
                      </div>
                    </div>
                  </div>
                  {/* AI YHTEENVETO */}
                  <div className={styles.detailSection}>
                    <strong className={styles.aiTitle}>
                      <IconWizard /> Yhteenveto
                    </strong>
                    <p className={styles.summaryText}>
                      {selectedDoc.ai?.summary
                        ? selectedDoc.ai.summary.length > 500
                          ? selectedDoc.ai.summary.slice(0, 500) + "…"
                          : selectedDoc.ai.summary
                        : "AI-analyysi on käynnissä tai ei vielä saatavilla."}
                    </p>
                  </div>

                  {/* AI KEY POINTS */}
                  {(selectedDoc.ai?.keyPoints || []).length > 0 && (
                    <div className={styles.detailSection}>
                      <strong className={styles.aiTitle}>
                        <IconWizard /> Keskeiset asiat
                      </strong>
                      <div className={styles.keyPointsList}>
                        {selectedDoc.ai.keyPoints
                          .slice(0, 5)
                          .map((point, idx) => (
                            <div
                              key={idx}
                              className={styles.keyPointRow}
                            >
                              <div
                                className={styles.keyPointIndex}
                              >
                                {idx + 1}
                              </div>
                              <div
                                className={styles.keyPointText}
                              >
                                {point}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {selectedDoc.url && (
                    <a
                      href={selectedDoc.url}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.openBtn}
                    >
                      Avaa dokumentti
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUpload}
        categories={CATEGORY_OPTIONS}
      />
    </div>
  );
}
